/* eslint-disable camelcase */
import React, { useMemo, useEffect } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
// 1. Swap Polyglot for i18next
import { useTranslation } from 'react-i18next';
import api from '../api';
import Modal from './Modal';
import { SecondaryButton } from './buttons';
import './ProfileDeleteModal.scss';

function ProfileDeleteModal({
  isOpen,
  isCurrentProfile,
  isLoading,
  onCancel,
  onConfirm,
  profileId,
  error = null,
  resetDeleteState
}) {
  // 2. Initialize the translation hook
  const { t } = useTranslation();
  
  // Reset the error/loading state whenever the target profile changes
  useEffect(() => {
    resetDeleteState();
  }, [profileId, resetDeleteState]);

  // 3. useMemo remains efficient but now tracks the new t function
  const modalCopy = useMemo(() => {
    if (error) {
      return <p className="profile-delete-modal__error">{error.message}</p>;
    }
    if (isCurrentProfile) {
      return <p>{t('profile.deleteCurrentMessage')}</p>;
    }
    return (
      <>
        <p className="profile-delete-modal__confirmation">
          {t('profile.deleteConfirmation')}
        </p>
        <p>{t('profile.deleteWarning')}</p>
      </>
    );
  }, [error, isCurrentProfile, t]);

  return (
    <Modal
      className="profile-delete-modal"
      isOpen={isOpen}
      showCloseButton={false}
      onRequestClose={onCancel}
    >
      {modalCopy}

      {isCurrentProfile || error ? (
        <div className="profile-delete-modal__actions profile-delete-modal__actions--single">
          <SecondaryButton
            colorScheme="blue"
            disabled={isLoading}
            type="button"
            onClick={onCancel}
          >
            {t('profile.deleteCurrentCancel')}
          </SecondaryButton>
        </div>
      ) : (
        <div className="profile-delete-modal__actions">
          <SecondaryButton
            colorScheme="blue"
            disabled={isLoading}
            type="button"
            onClick={onCancel}
          >
            {t('profile.deleteCancel')}
          </SecondaryButton>
          <SecondaryButton
            colorScheme="red"
            disabled={isLoading || !!error}
            loading={isLoading}
            type="button"
            onClick={() => onConfirm(profileId)}
          >
            {t('profile.deleteConfirm')}
          </SecondaryButton>
        </div>
      )}
    </Modal>
  );
}

ProfileDeleteModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  isCurrentProfile: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  profileId: PropTypes.string.isRequired,
  error: PropTypes.shape({
    message: PropTypes.string
  }),
  resetDeleteState: PropTypes.func.isRequired
};

const mapStateToProps = state => ({
  error: state.profilesDelete.error
});

const mapDispatchToProps = dispatch => ({
  resetDeleteState: () => dispatch(api.actions.profilesDelete.reset())
});

// 4. Export the connected component without the translate HOC
export default connect(mapStateToProps, mapDispatchToProps)(ProfileDeleteModal);
